import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import Button from 'components/partials/button/button';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import TableComponent from 'components/partials/table-component/table-component';
import type { TableAmountState, TableColumnDef } from 'components/partials/table-component/table-component';
import toast from 'components/partials/toast/toast';

import SelectFundsModal from './components/select-funds-modal';
import { getFundsTableSettings } from './funds-table-settings';
import { AWARDING_TYPE_OPTIONS } from './funds-step.constants';

import {
  useModelSpecialFunds,
  useCreateModelSpecialFund,
  useUpdateModelSpecialFund,
  useDeleteModelSpecialFund,
  aidSpecialFundsKeys,
} from '../../hooks/use-aid-special-funds';
import { useFundsDraft } from './hooks/use-funds-draft';

import { useDictionaryByType } from 'hooks/use-dictionary-by-type';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import Icons from 'assets/icons';

import type { Fund, FundCandidate } from './funds-step.types';
import type { SortingState } from '@tanstack/react-table';

const PAGE_SIZE = 10;

const transformFundsWithSubRows = (funds: Fund[]): Record<string, unknown>[] =>
  funds.map((fund, fundIndex) => {
    const fundKey = fund.id || fund._tempId;
    const awards = fund.awards || [];

    return {
      ...fund,
      fundIndex,
      isParent: true,
      childCount: awards.length,
      subRows: awards.map((award, awardIndex) => ({
        id: `${fundKey}-award-${awardIndex}`,
        parentId: fundKey,
        fundIndex,
        awardIndex,
        name: `Award amount ${awardIndex + 1}`,
        previousYearAmount: award.previousYearAmount,
        previousYearRecipients: award.previousYearRecipients,
        currentYearAmount: award.currentYearAmount,
        awardingType: fund.awardingType,
        isParent: false,
      })),
    };
  });

interface FundsStepProps {
  stepActionsSlotId: string;
}

const FundsStep = forwardRef<unknown, FundsStepProps>(
  ({ stepActionsSlotId }, ref) => {
  const { clientId, modelId } = useParams();
  const currentUser = useCurrentUser() as unknown as {
    data?: { oktaUserId?: string };
  };
  const userId = currentUser?.data?.oktaUserId;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isSaving, setIsSaving] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sortingParams, setSortingParams] = useState<SortingState>([]);
  const [awardingTypeOptions, setAwardingTypeOptions] = useState<
    { value: string; label: string }[]
  >(AWARDING_TYPE_OPTIONS as { value: string; label: string }[]);

  const awardingTypeDictionaryQuery = useDictionaryByType(
    userId ?? '',
    'AWARDING_TYPE',
    { enabled: !!userId },
  );

  useEffect(() => {
    if (stepActionsSlotId) {
      setPortalTarget(document.getElementById(stepActionsSlotId));
    }
  }, [stepActionsSlotId]);

  const fundsQuery = useModelSpecialFunds({
    userId: userId ?? '',
    modelId: modelId ?? '',
    enabled: !!modelId,
  });
  const serverFunds = fundsQuery.data as Fund[] | null | undefined;
  const isLoading = fundsQuery.isLoading;

  useEffect(() => {
    const dictionary = awardingTypeDictionaryQuery.data;

    if (!dictionary?.length) {
      return;
    }

    const options = dictionary
      .map((item) => ({
        value: item.name ?? '',
        label: item.description ?? item.name ?? '',
      }))
      .filter((option) => !!option.value);

    if (options.length > 0) {
      setAwardingTypeOptions(options);
    }
  }, [awardingTypeDictionaryQuery.data]);

  const queryClient = useQueryClient();
  const createFund = useCreateModelSpecialFund();
  const updateFund = useUpdateModelSpecialFund();
  const deleteFund = useDeleteModelSpecialFund();

  const {
    funds,
    hasChanges,
    assignedFundIds,
    addFunds,
    removeFund,
    updateAwardingType,
    updateAwardAmount,
    reset,
    markForReinit,
    computeChanges,
  } = useFundsDraft(serverFunds);

  const tableData = useMemo(
    () => transformFundsWithSubRows((funds ?? []) as Fund[]),
    [funds]
  );

  const getSubRows = useCallback(
    (row: { subRows?: unknown[] }) => row.subRows || [],
    []
  );

  const getRowCanExpand = useCallback(
    (row: { original: { isParent?: boolean; subRows?: unknown[] } }) =>
      Boolean(
        row.original.isParent && (row.original.subRows?.length ?? 0) > 0
      ),
    []
  );

  const handleTableSetAmount = useCallback(
    (updater: (state: TableAmountState) => TableAmountState): void => {
      const next = updater({ pageSize, totalRecords: funds.length });

      setPageSize(next.pageSize);
      setPageIndex(0);
    },
    [funds.length, pageSize]
  );

  const handleSave = useCallback(async (): Promise<void> => {
    if (!hasChanges) return;

    setIsSaving(true);

    try {
      const { toCreate, toUpdate, toDelete } = computeChanges() as {
        toCreate: Record<string, unknown>[];
        toUpdate: { id: string | number; [key: string]: unknown }[];
        toDelete: (string | number)[];
      };

      await Promise.all(
        toDelete.map((fundId) =>
          deleteFund.mutateAsync({
            userId: userId ?? '',
            modelId: modelId ?? '',
            fundId,
          })
        )
      );

      await Promise.all(
        toUpdate.map(({ id, ...data }) =>
          updateFund.mutateAsync({
            userId: userId ?? '',
            modelId: modelId ?? '',
            fundId: id,
            data,
          })
        )
      );

      await Promise.all(
        toCreate.map((data) =>
          createFund.mutateAsync({
            userId: userId ?? '',
            modelId: modelId ?? '',
            data,
          })
        )
      );

      await queryClient.invalidateQueries({
        queryKey: aidSpecialFundsKeys.model(modelId ?? ''),
      });

      markForReinit();

      toast.success({
        title: 'Funds updated',
        message: 'Special funds have been saved successfully',
      });
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };

      toast.error({
        title: 'Error saving funds',
        message: err?.response?.data?.message || 'An error occurred',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    hasChanges,
    computeChanges,
    markForReinit,
    queryClient,
    userId,
    modelId,
    createFund,
    updateFund,
    deleteFund,
  ]);

  useImperativeHandle(ref, () => ({ save: handleSave, hasChanges }), [
    handleSave,
    hasChanges,
  ]);

  const handleAddFunds = useCallback(
    (selected: FundCandidate[]): void => {
      addFunds(selected);
      setIsModalOpen(false);
    },
    [addFunds]
  );

  const columns = useMemo<TableColumnDef<Record<string, unknown>>[]>(
    () =>
      getFundsTableSettings({
        onDelete: removeFund,
        onAwardingTypeChange: updateAwardingType,
        onAwardAmountChange: updateAwardAmount,
        awardingTypeOptions,
      }) as TableColumnDef<Record<string, unknown>>[],
    [removeFund, updateAwardingType, updateAwardAmount, awardingTypeOptions]
  );

  if (!modelId) {
    return (
      <div className="py-8 text-center text-gray-600">
        Please save the model first to manage funds.
      </div>
    );
  }

  const stepActions = portalTarget
    ? createPortal(
        <>
          <Button
            type="button"
            variant="reserve-text"
            size="sm"
            onClick={reset}
            disabled={!hasChanges}
            data-testid="discard-changes-button"
          >
            Discard changes
          </Button>
          <Button
            type="button"
            variant="brand-filled"
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            loading={isSaving}
            data-testid="save-funds-button"
          >
            Save
          </Button>
        </>,
        portalTarget
      )
    : null;

  return (
    <div data-testid="funds-step">
      {stepActions}

      {funds.length === 0 && !isLoading ? (
        <EmptySpaceContent
          image={<Icons.EmptyImage customClass="w-32 h-32" />}
          text="No funds added yet"
          linkShow
          linkText="Add funds"
          onClick={() => setIsModalOpen(true)}
        />
      ) : (
        <>
          <TableComponent
            columns={columns}
            data={tableData}
            isLoading={isLoading}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPagination={setPageIndex}
            setAmount={handleTableSetAmount}
            sortingParams={sortingParams}
            setSortingParams={setSortingParams}
            manualSorting={false}
            manualPagination={false}
            totalRecords={funds.length}
            pagination={funds.length > PAGE_SIZE}
            expandable
            expanded={expanded as never}
            onExpandedChange={setExpanded as never}
            getSubRows={getSubRows as never}
            getRowCanExpand={getRowCanExpand as never}
            customHeight="max-h-[50vh]"
            paginateExpandedRows={false}
          />

          <div className="mt-4">
            <Button
              type="button"
              variant="reserve-outlined"
              size="sm"
              onClick={() => setIsModalOpen(true)}
              iconAfter={<Icons.Plus />}
              className="shadow"
              data-testid="add-funds-button"
            >
              Add funds
            </Button>
          </div>
        </>
      )}

      {isModalOpen && (
        <SelectFundsModal
          userId={userId ?? ''}
          clientId={clientId ?? ''}
          modelId={modelId ?? ''}
          excludeIds={assignedFundIds}
          onAdd={handleAddFunds}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
  }
);

FundsStep.displayName = 'FundsStep';

export default FundsStep;
